import React, { useReducer, useEffect, useState, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import { ConnectButton, useWalletKit } from "@mysten/wallet-kit";
import { OBJECT_RECORD, LAMPORT } from "../config";
import {
  useGetBalance,
  useStakingMethods,
  useGetPresaleInfo,
  useGetAccountInfo,
} from "../hooks";
import BigNumber from "bignumber.js";
import { convertFloat } from "../utils";
import { useStakings } from "../context";
import { LinearProgress } from "@mui/material";
import { SuiLogo } from "../components/sui";

export const PresalePage = () => {
  const { currentAccount } = useWalletKit();
  const { state, changeVariable } = useStakings();

  const [updateNumber, forceUpdate] = useReducer((x) => x + 1, 0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      forceUpdate();
    }, 10000);

    return () => clearInterval(intervalId);
  }, []);

  const { staking, claim } = useStakingMethods();

  const currentBalance = useGetBalance(
    currentAccount?.address || OBJECT_RECORD.ADDRESSZERO,
    updateNumber
  );

  const { status: presaleStatus, poolInfo: presaleInfo } = useGetPresaleInfo(updateNumber)
  const userState = useGetAccountInfo(currentAccount?.address, updateNumber)

  useEffect(() => {
    if (userState) {
      changeVariable("purchased", userState["purchasedAmount"]);
      changeVariable("isClaimed", userState["isClaimed"]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userState]);


  useEffect(() => {
    if (presaleInfo) {
      changeVariable("startTime", presaleInfo["startTime"] || Date.now());
      changeVariable("endTime", presaleInfo["endTime"] || Date.now());
      changeVariable("currentRate", presaleInfo["tokenPerSui"]);
      changeVariable("totalSupply", presaleInfo["totalSupply"]);
      changeVariable("allocated", presaleInfo["allocated"]);
      changeVariable("isClaimable", presaleInfo["isClaimable"]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presaleInfo, presaleStatus]);

  const [address, setAddress] = useState<any>("");

  useEffect(() => {
    if (currentAccount?.address) {
      let tempAddr = currentAccount?.address;
      setAddress(tempAddr.slice(0, 4) + "..." + tempAddr.slice(-4));
    } else setAddress("");
  }, [currentAccount]);

  const [amount, setAmount] = useState<any>("0");
  const maxFunction = () => {
    setAmount(
      currentAccount?.address && parseFloat(currentBalance) > 0.3
        ? convertFloat(parseFloat(currentBalance) - 0.1)
        : 0
    );
  };

  const handleStake = async (amount) => {
    let tx = await staking(amount);
    let status: string = tx!["effects"]!["status"]!["status"] || "failure";
    let error: string =
      tx!["effects"]!["status"]!["error"] ||
      "We are aorry. Please try later again.";

    if (status === "failure") {
      toast(error, {
        type: "error",
      });
    } else {
      toast("Buying success", {
        type: "success",
      });
    }
  };

  const handleClaim = async () => {
    let tx = await claim();
    let status: string = tx!["effects"]!["status"]!["status"] || "failure";
    let error: string =
      tx!["effects"]!["status"]!["error"] ||
      "We are aorry. Please try later again.";

    if (status === "failure") {
      toast(error, {
        type: "error",
      });
    } else {
      toast("Claiming success", {
        type: "success",
      });
    }
  };

  const currentRaised = state["totalSupply"] || state["totalSupply"] !== 0 ? convertFloat((state["allocated"]).dividedBy(state["totalSupply"])) : BigNumber(0)
  const currentRaisedNum = !isNaN(Number(currentRaised)) ? Number(currentRaised) * 100 : 0

  const claimableToken = state["purchased"] || state["purchased"] !== 0 ? convertFloat(Number(state["purchased"]) * state["currentRate"]) : BigNumber(0)

  const now = Date.now()

  const [title, endTime, presaleFlag] = useMemo(() => {
    if (!state.startTime) {
      return ['Presale Starts In', now, 'Upcoming']
    }
    if (now < state.startTime) return ['Presale Starts In', state.startTime, 'Upcoming']
    if (now < state.endTime) return ['Presale Ends In', state.endTime, 'Sale live']
    return ['Presale Ended', state.endTime, 'Ended']
  }, [now, state.endTime, state.startTime])

  const [days, setDays] = useState("00");
  const [hours, setHours] = useState("00");
  const [minutes, setMinutes] = useState("00");
  const [seconds, setSeconds] = useState("00");

  const getTimeRemaining = (e) => {
    const total = e - Date.now();
    const seconds = Math.floor((total / 1000) % 60);
    const minutes = Math.floor((total / 1000 / 60) % 60);
    const hours = Math.floor((total / 1000 / 60 / 60) % 24);
    const days = Math.floor((total / 1000 / 60 / 60 / 24));
    return {
      total,
      days,
      hours,
      minutes,
      seconds,
    };
  };

  const startTimer = (e) => {
    let { total, hours, minutes, seconds, days } = getTimeRemaining(e);
    if (total >= 0) {
      setDays(days.toString())
      setHours(hours < 9 ? '0' + hours : hours.toString())
      setMinutes(minutes < 9 ? '0' + minutes : minutes.toString())
      setSeconds(seconds < 9 ? '0' + seconds : seconds.toString())
    }
  };

  const Ref = useRef(null);
  const clearTimer = (e) => {
    setDays('00')
    setHours('00')
    setMinutes('00')
    setSeconds('00')
    if (Ref.current) clearInterval(Ref.current);
    const id = setInterval(() => {
      startTimer(e);
    }, 1000);
    Ref.current = id;
  };

  useEffect(() => {
    clearTimer(endTime)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endTime])

  return (
    <>
      <div className="flex-1 flex flex-col gap-50 items-center">
        <div className="flex flex-col gap-30 items-center">
          <div className="flex flex-col items-center text-center">
            <span className="hero-title">
              {title}
            </span>
          </div>

          <div className="flex flex-row gap-20 flex-wrap justify-center">
            <div className="w-64 lg:w-100 flex flex-col gap-5 mm:gap-10 lg:gap-15 bg-[#fef2b9] py-10 mm:py-15 lg:py-20 rounded-[2.1875rem] text-center box-shadow-paw">
              <span className="text-14 mm:text-18 font-bold">
                {days}
              </span>
              <span className="text-10 mm:text-14 opacity-50">
                Days
              </span>
            </div>
            <div className="w-64 lg:w-100 flex flex-col gap-5 mm:gap-10 lg:gap-15 bg-[#fef2b9] py-10 mm:py-15 lg:py-20 rounded-[2.1875rem] text-center box-shadow-paw">
              <span className="text-14 mm:text-18 font-bold">
                {hours}
              </span>
              <span className="text-10 mm:text-14 opacity-50">
                Hours
              </span>
            </div>
            <div className="w-64 lg:w-100 flex flex-col gap-5 mm:gap-10 lg:gap-15 bg-[#fef2b9] py-10 mm:py-15 lg:py-20 rounded-[2.1875rem] text-center box-shadow-paw">
              <span className="text-14 mm:text-18 font-bold">
                {minutes}
              </span>
              <span className="text-10 mm:text-14 opacity-50">
                Minutes
              </span>
            </div>
            <div className="w-64 lg:w-100 flex flex-col gap-5 mm:gap-10 lg:gap-15 bg-[#fef2b9] py-10 mm:py-15 lg:py-20 rounded-[2.1875rem] text-center box-shadow-paw">
              <span className="text-14 mm:text-18 font-bold">
                {seconds}
              </span>
              <span className="text-10 mm:text-14 opacity-50">
                Seconds
              </span>
            </div>
          </div>

          <div className="w-full max-w-500 flex flex-col gap-15 md:gap-18 lg:gap-20 px-15 md:px-18 lg:px-20 py-15 md:py-18 lg:py-20 bg-[#fef2b9] rounded-[2.1875rem] box-shadow-paw">
            <div className="flex flex-col px-2 pt-10 md:pt-12 lg:pt-2 rounded-md">
              <LinearProgress value={Math.round(currentRaisedNum)} variant="determinate" className="progress-bar" />
            </div>
            <div className="flex flex-row gap-6 justify-between items-center text-14">
              <div className="flex flex-row items-center">
                {Number(state["allocated"]).toLocaleString()} SSSPAW
              </div>
              <div className="flex flex-row items-center">
                {Number(state["totalSupply"]).toLocaleString()} SSSPAW
              </div>
            </div>
            {presaleFlag !== 'Ended' && <div className="flex flex-col px-15 md:px-18 lg:px-20 py-10 md:py-12 lg:py-15 bg-[#fef2b9] rounded-md">
              <div className="flex flex-row gap-10 justify-between items-center py-10 md:py-15 lg:py-20">
                <div className="flex flex-row gap-2 md:gap-6 items-center">
                  <div className="w-20 md:w-24 h-20 md:h-30">
                    <SuiLogo />
                  </div>
                  <span className="flex text-15 md:text-18 lg:text-20 font-semibold items-start">
                    SUI
                  </span>
                </div>

                <div className="flex flex-row gap-10 items-center">
                  <span
                    className="text-15 md:text-18 lg:text-20 font-bold text-[#f7b4c0] cursor-pointer"
                    onClick={maxFunction}
                  >
                    MAX
                  </span>
                </div>
              </div>

              <div className="border-t-2 border-black py-5 md:py-8 lg:py-10">
                <div className="flex flex-row gap-10 text-12 md:text-15 opacity-50 items-center">
                  <input
                    placeholder="Amount"
                    className="text-left flex-1 text-black"
                    type="number"
                    value={amount}
                    step={0.001}
                    onChange={(e) => {
                      let value = Number(e.target.value);
                      setAmount(value)
                    }}
                  />
                  SUI
                </div>
              </div>
            </div>
            }

            {!!address && (
              presaleFlag !== 'Ended' ?
                <button
                  onClick={() => {
                    handleStake(amount * LAMPORT);
                  }}
                  className="bg-[#fef2b9] px-10 py-10 md:py-15 rounded-lg text-center cursor-pointer text-12 md:text-15 font-semibold box-shadow-paw"
                  disabled={presaleFlag !== 'Sale live' || Number(amount) === 0 || Number(amount) > parseFloat(currentBalance) - 0.1}
                >
                  {presaleFlag !== 'Sale live' ? 'Upcoming' : 'Buy'}
                </button> :
                <button
                  onClick={() => { handleClaim(); }}
                  className="bg-[#fef2b9] px-10 py-10 md:py-15 rounded-lg text-center cursor-pointer text-12 md:text-15 font-semibold box-shadow-paw"
                  disabled={!(Number(state["allocated"]) > 0) || !state.isClaimable || state.isClaimed}
                >
                  {!state.isClaimed ? 'Claim' : 'You have already claimed'}
                </button>
            )}

            {!address && (
              <ConnectButton
                connectedText={address}
                connectText={"Connect Wallet"}
                className="btn-connect px-10 py-10 md:py-15 rounded-lg text-center cursor-pointer text-12 md:text-15 font-semibold box-shadow-paw"
              />
            )}
          </div>
          <div className="w-full max-w-500 flex flex-col gap-10 md:gap-12 lg:gap-14 px-15 md:px-18 lg:px-20 py-15 md:py-18 lg:py-20 bg-[#fef2b9] rounded-[2.1875rem] box-shadow-paw">
            <div className="flex flex-row gap-6 justify-between items-center">
              <div className="flex flex-row gap-4 md:gap-6 items-center opacity-50">
                <span className="text-14 md:text-16 lg:text-16">
                  Status
                </span>
              </div>
              <div className="flex flex-row gap-6 items-center">
                <span className="text-14 md:text-14">
                  {presaleFlag}
                </span>
              </div>
            </div>
            <div className="flex flex-row gap-6 justify-between items-center">
              <div className="flex flex-row gap-4 md:gap-6 items-center opacity-50">
                <span className="text-14 md:text-16 lg:text-16">
                  Current Rate
                </span>
              </div>
              <div className="flex flex-row gap-6 items-center">
                <span className="text-14 md:text-14">
                  1 SUI = {Number(state["currentRate"]).toLocaleString()} SSSPAW
                </span>
              </div>
            </div>
            <div className="flex flex-row gap-6 justify-between items-center">
              <div className="flex flex-row gap-4 md:gap-6 items-center opacity-50">
                <span className="text-14 md:text-16 lg:text-16">
                  Current Raised
                </span>
              </div>
              <div className="flex flex-row gap-6 items-center">
                <span className="text-14 md:text-14">
                  {(!isNaN(state.allocated / state.currentRate) ? state.allocated / state.currentRate : 0).toLocaleString()} SUI ({convertFloat(currentRaisedNum)}%)
                </span>
              </div>
            </div>
            <div className="flex flex-row gap-6 justify-between items-center">
              <div className="flex flex-row gap-4 md:gap-6 items-center opacity-50">
                <span className="text-14 md:text-16 lg:text-16">
                  Claimable
                </span>
              </div>
              <div className="flex flex-row gap-6 items-center">
                <span className="text-14 md:text-14">
                  {Number(claimableToken).toLocaleString()} SSSPAW
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
